/**
 * Historical era scene — the Bronze Age Kaaba (~2000–1800 B.C.E), the oldest
 * card in the Kaaba's historical-scenes slider (docs/architecture/scene-registry.md).
 *
 * A STANDALONE `SceneConfig` (authored like arafat / mina — NOT derived from
 * `kaabaSceneConfig`): its splat is a fresh world-scale export with its own
 * camera / controls / environment. It is only a "historical scene"
 * navigationally — a child of `kaaba` on the stack (`[kaaba, era]`), entered
 * via the slider's Explore CTA — and a FULL scene with chat + POI: its id is
 * whitelisted for `scene_open` (see `BACKEND_ERA_SCENE_IDS` in `registry.ts`),
 * so the backend supplies its intro card + `tour` (POI chips + 3D markers).
 *
 * Splat transform: NONE (identity) — world-scale export like mina / muzdalifah.
 *
 * The camera poses + lighting below started from the modern Kaaba's (they framed
 * this model acceptably once the transform was dropped), but this scene owns
 * them outright now — no flyover, and free-orbit controls (Kaaba's height /
 * vertical clamps removed so the camera tilts freely). Fine-tune poses via
 * `?dev` → Camera and copy final values back here.
 */
import type { SceneConfig } from '../sceneConfig';
import { resolveCdnUrl } from '../../utils/cdnUrl';

// v8 is another modeller re-capture (delivered as `BAK_*`, renamed on the way in
// — the repo's `_vN` counts its own re-exports; the source name carries no
// version). Decoded means on the 600K tier: geometry held — around the scene
// centre the ground sits at y 0.07 (v7: 0.12) and the structure tops out at 1.01
// (v7: 1.02), full bbox x[-141, 115] y[-49, 19] z[-156, 140] against v7's
// x[-140, 116] y[-47, 21] z[-153, 142] — so camera poses, colliders, POI anchors
// and the v7 preloader cloud carry over untouched. Splat counts are within 0.1 %
// per tier (598K / 995K / 1.99M). The filename MUST change with the content: the
// CDN serves models with `immutable, max-age=31536000`, so a reused name would
// never refresh for anyone who already has it.
const KAABA_2000BCE_2M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_2000BCE_v8_2M.sog');
const KAABA_2000BCE_1M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_2000BCE_v8_1M.sog');
const KAABA_2000BCE_600K_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_2000BCE_v8_600K.sog');

const TARGET_POINT = { x: -1.05, y: 0.35, z: -0.81 };

export const kaaba2000BceSceneConfig: SceneConfig = {
  id: 'kaaba-2000bce',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 75,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: -1.52, y: 1.03, z: 0.76 },
    previewTourStart: { x: -6.92, y: 3.75, z: 3.85 },
    previewTourEnd: {
      position: { x: 4.15, y: 1.81, z: 4.08 },
      target: TARGET_POINT,
    },
  },
  controls: {
    // Free orbit — no height / vertical clamps (unlike the modern Kaaba) so the
    // camera tilts and zooms out freely while framing is dialed in.
    orbitMinDistance: 0,
    orbitMaxDistance: 10000,
    orbitMinPolarAngle: 0,
    // Cap the lowest tilt at polar 87° — the camera stays a hair above
    // horizontal and never looks up at the enclosure from below the ground.
    orbitMaxPolarAngle: (87 / 180) * Math.PI,
    constrainVertical: false,
    clampMinHeight: -Infinity,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitCenter: TARGET_POINT,
  },
  // Collision (tuned via the ?dev collision panel):
  //  - `kaaba-cube`: solid OBB around the Kaaba structure — camera stays outside.
  //  - `scene-boundary`: hollow cylinder cage — camera stays inside the scene.
  colliders: [
    {
      id: 'kaaba-cube',
      type: 'box',
      mode: 'solid',
      enabled: true,
      position: { x: -0.9709499216898674, y: 0.1708300271585894, z: -0.8054852677447097 },
      size: { x: 3.5, y: 1.7, z: 2.7 },
      rotation: { x: 0, y: 1.5882496193148399, z: 0 },
    },
    {
      id: 'scene-boundary',
      type: 'cylinder',
      mode: 'hollow',
      enabled: true,
      blocksLineOfSight: false,
      position: { x: -1.0418837155173448, y: 0.24, z: -0.7924066976394661 },
      radius: 9,
      height: 7,
    },
  ],
  splat: {
    defaultByQuality: {
      desktop: 'kaaba_2000bce_2m',
      mobile: { high: 'kaaba_2000bce_1m', middle: 'kaaba_2000bce_1m', low: 'kaaba_2000bce_600k' },
    },
    // No transform → identity (world-scale export, like mina / muzdalifah).
    variants: [
      { key: 'kaaba_2000bce_2m', url: KAABA_2000BCE_2M_URL },
      { key: 'kaaba_2000bce_1m', url: KAABA_2000BCE_1M_URL },
      { key: 'kaaba_2000bce_600k', url: KAABA_2000BCE_600K_URL },
    ],
    revealAnimationDuration: 3,
    revealDistanceScale: 0.8,
    revealMode: '3d',
    revealOrigin: TARGET_POINT,
  },
  previewModel: {
    // Still the v7 export — v8 shipped splats only, and the geometry it morphs
    // into did not move. Same point count as the v5 cloud but cropped tighter
    // (~12 world units across against ~16), so it reads ~1.8x denser, and baked
    // ~10 % brighter.
    url: resolveCdnUrl('preview/Kaaba_2000BCE_v7_preloader_pnts_150000_meshopt(17).glb'),
    style: {
      pointSizeFactor: 2.5,
      wavesCenter: TARGET_POINT,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
  // The capture's own bird was removed for v6 and stays absent in v7 (checked:
  // one stray point above y 1.1 in the whole core); this is its animated
  // replacement, perched on the well where the original sat. Pose found with the
  // legacy crow tuner gizmo.
  crowProp: {
    // The v7 recapture moved the well +0.27 in x and +0.34 in z (rim top
    // unchanged at 0.41, measured off the 600K tier against the v6 export), so
    // the perch and the two route beats that belong to the well moved with it.
    // Everything else in the scene held still — enclosure x [-2.29, 0.51],
    // z [-2.57, 0.86], wall tops 1.008 in both exports.
    position: { x: 2.64, y: 0.64, z: 2.93 },
    length: 0.35,
    sightId: 'bird-2000-bce',
    // Opening cinematic. Six beats: off the well, a sweep down the near flank
    // of the enclosure, up over its west end, back down onto the archway's
    // axis, THROUGH the arch, out over the far wall and home to the well.
    //
    // Every coordinate below was measured in the running scene with
    // `__crowTour.pick(x, y)` (`?dev`), which casts a ray through a viewport
    // pixel at the `kaaba-cube` OBB. Splats cannot be raycast, so this is the
    // only way to read a feature's position off the model — do not adjust these
    // by eye. Geometry they depend on:
    //   · archway centre  (-2.306, 0.504, 0.072), gap 0.48 wide × 0.55 tall
    //   · its inward axis  heading 1.5882 = the `kaaba-cube` y-rotation
    //   · wall tops        y 1.02        · enclosure  x [-2.35, 0.41], z [-2.58, 0.97]
    tour: {
      waypoints: [
        // Both ends are overwritten with the bird's live perch, so the take-off
        // and the landing follow the perch wherever it is set.
        { x: 2.64, y: 0.64, z: 2.93 },
        // Off the well, banking away over the open ground.
        { x: 1.29, y: 1.18, z: 2.46 },
        // The flank sweep, clear of the south wall (z 0.97) and above the top.
        { x: -1.35, y: 1.52, z: 1.72 },
        // Up and out past the west end.
        { x: -3.65, y: 2.06, z: 0.95 },
        // Turning back down, still wide of the wall (1.85 clear of its west
        // face at x -2.29). Pulled in from -4.42 when the perch moved for v7:
        // the route's centripetal knots re-time globally, and the hairpin here
        // is the sharpest turn on the whole flight — it landed exactly on the
        // 0.2 rad/frame guard in `crowTour.test.ts`. Note the direction, it is
        // not intuitive: pushing this point FURTHER out makes the turn sharper
        // (-4.55 measures 0.231), pulling it in is what buys margin (0.165).
        { x: -4.2, y: 1.02, z: 0.2 },
        // These three are COLLINEAR on the archway's inward axis and share its
        // height, so the spline runs dead straight through the gap. Nudging any
        // of them off the line clips a jamb.
        { x: -3.306, y: 0.504, z: 0.089 },
        { x: -2.306, y: 0.504, z: 0.072 },
        // Only 0.45 past the gap. Further in and the bird holds level down the
        // length of a 2.7-deep roofless box: the camera sits inside it too, and
        // the shot goes to a dark trench for a second and a half. Pulling up
        // here turns that into the walls dropping away beneath.
        { x: -1.856, y: 0.504, z: 0.064 },
        // Up out of the enclosure. The east wall has an opening of its own, but
        // it measures 0.25 × 0.20 against a 0.35 bird — too small to fly, so the
        // way out is over the top.
        { x: -0.95, y: 0.95, z: 0.03 },
        { x: 0.55, y: 1.45, z: -0.12 },
        // Curving back round to the well.
        { x: 2.51, y: 1.16, z: 1.58 },
        { x: 2.64, y: 0.64, z: 2.93 },
      ],
      duration: 18,
      // Two body lengths back — the bird is 0.35.
      cameraDistance: 0.7,
      // Low enough that the bird stays near frame centre through the dive — at
      // 0.25 the camera looks down its own path and the bird rides the bottom
      // edge just where the shot should be reading it.
      cameraHeight: 0.18,
      entrySeconds: 2.5,
      handBackSeconds: 2.2,
      // The archway beat. `distance` must stay under the 0.48 gap or the camera
      // clips the masonry on its way through; `height` drops to nothing so it
      // goes through level rather than into the lintel.
      closeUp: {
        // Measured, not guessed: the bird is nearest the archway centre at
        // 0.5477 (0.0003 units off it in plan). A test pins this to the actual
        // crossing, because a close-up half a second early tightens on masonry.
        // Re-measure after ANY waypoint edit — the centripetal knots redistribute
        // time along the whole route, so moving a later point shifts this. The
        // v7 perch move plus the eased hairpin cost 0.0043 here.
        at: 0.5477,
        span: 0.22,
        distance: 0.3,
        height: 0.02,
        fov: 52,
      },
    },
  },
};
